import React from "react";

// ---------------------------------------------------------------------------
// Single knob: bump this to scale the entire mobile UI up or down.
// 1.0 = desktop baseline, 1.5 = +50%, 2.0 = 2x, etc.
// ---------------------------------------------------------------------------
export const MOBILE_SCALE = 1.3;

// Desktop baseline values (tweak these rarely).
const BASE = {
  body: 14,
  h1: 18,
  h2: 16,
  h3: 15,
  icon: 20,
  button: 14,
  messageBar: 12,
  avatar: 24,
  input: 14,
  minTouch: 30,
  pageHeaderAside: 30,
};

const px = (n: number) => `${Math.round(n * MOBILE_SCALE)}px`;

export const MOBILE_BODY_CLASS = "fileops-mobile";

const CSS = `
/* Body text + comfortable line-height so wrapped lines don't cram together */
body.${MOBILE_BODY_CLASS} * {
  font-size: ${px(BASE.body)} !important;
  line-height: 1.3 !important;
}

body.${MOBILE_BODY_CLASS} h1 { font-size: ${px(BASE.h1)} !important; }
body.${MOBILE_BODY_CLASS} h2 { font-size: ${px(BASE.h2)} !important; }
body.${MOBILE_BODY_CLASS} h3 { font-size: ${px(BASE.h3)} !important; }

/* Icons */
body.${MOBILE_BODY_CLASS} svg,
body.${MOBILE_BODY_CLASS} i[data-icon-name],
body.${MOBILE_BODY_CLASS} .ms-Icon,
body.${MOBILE_BODY_CLASS} .fui-Button__icon,
body.${MOBILE_BODY_CLASS} .fui-MenuItem__icon {
  width: ${px(BASE.icon)} !important;
  height: ${px(BASE.icon)} !important;
  font-size: ${px(BASE.icon)} !important;
  line-height: 1 !important;
}

/* Buttons (hit-area). No flex-wrap on toolbar — let Fluent's Overflow handle it. */
body.${MOBILE_BODY_CLASS} button,
body.${MOBILE_BODY_CLASS} [role='button'],
body.${MOBILE_BODY_CLASS} .fui-Button,
body.${MOBILE_BODY_CLASS} .fui-ToolbarButton,
body.${MOBILE_BODY_CLASS} .fui-MenuItem {
  min-height: ${px(BASE.minTouch)} !important;
  min-width: ${px(BASE.minTouch)} !important;
  padding: 8px 10px !important;
}

body.${MOBILE_BODY_CLASS} .fui-Toolbar,
body.${MOBILE_BODY_CLASS} [role='toolbar'] {
  gap: 2px !important;
  flex-wrap: nowrap !important;
}

body.${MOBILE_BODY_CLASS} .fui-MenuList,
body.${MOBILE_BODY_CLASS} .fui-MenuPopover {
  padding: 6px !important;
}

/* MessageBar body — override the universal rule so all inline children match */
body.${MOBILE_BODY_CLASS} .fui-MessageBarBody,
body.${MOBILE_BODY_CLASS} .fui-MessageBarBody * {
  font-size: ${px(BASE.messageBar)} !important;
}

/* Avatar circle scales; initials scale proportionally (circle * 0.4) */
body.${MOBILE_BODY_CLASS} .fui-Avatar {
  width: ${px(BASE.avatar)} !important;
  height: ${px(BASE.avatar)} !important;
}
body.${MOBILE_BODY_CLASS} .fui-Avatar,
body.${MOBILE_BODY_CLASS} .fui-Avatar * {
  font-size: ${px(BASE.avatar * 0.4)} !important;
  line-height: 1 !important;
}
body.${MOBILE_BODY_CLASS} .fui-Avatar svg {
  width: ${px(BASE.avatar * 0.6)} !important;
  height: ${px(BASE.avatar * 0.6)} !important;
}

/* Inputs */
body.${MOBILE_BODY_CLASS} input,
body.${MOBILE_BODY_CLASS} .fui-Input,
body.${MOBILE_BODY_CLASS} .fui-Input__input {
  font-size: ${px(BASE.input)} !important;
  min-height: ${px(BASE.minTouch)} !important;
}
`;

let injected = false;
const ensureStylesInjected = () => {
  if (injected || typeof document === "undefined") return;
  const style = document.createElement("style");
  style.setAttribute("data-fileops", "mobile-scale");
  style.textContent = CSS;
  document.head.appendChild(style);
  injected = true;
};

export const useMobileScale = (isMobile: boolean) => {
  React.useEffect(() => {
    if (!isMobile || typeof document === "undefined") return;
    ensureStylesInjected();
    document.body.classList.add(MOBILE_BODY_CLASS);
    return () => {
      document.body.classList.remove(MOBILE_BODY_CLASS);
    };
  }, [isMobile]);
};

// Applies the mobile scale immediately on load — before OfficeContainer mounts.
// Needed so early pre-auth UI (Authenticating..., Checking licence...) scales.
export const applyMobileScaleEarly = () => {
  if (typeof document === "undefined") return;
  try {
    const OfficeAny = (window as unknown as { Office?: { context?: { mailbox?: { diagnostics?: { hostName?: string } } } } }).Office;
    const host = OfficeAny?.context?.mailbox?.diagnostics?.hostName;
    if (host === "OutlookIOS" || host === "OutlookAndroid") {
      ensureStylesInjected();
      document.body.classList.add(MOBILE_BODY_CLASS);
    }
  } catch {
    // Office not ready yet — harmless; useMobileScale will apply it later.
  }
};

// Width of the PageHeader icon column — scales with MOBILE_SCALE on mobile.
export const pageHeaderAsideWidth = (isMobile: boolean) =>
  isMobile ? Math.round(BASE.pageHeaderAside * MOBILE_SCALE) : BASE.pageHeaderAside;
